import React, { useCallback, useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import { admins, currentSeason } from "../../Constants";
import {
    RecomputeStatus, getRecomputeStatus, startRecompute, pauseRecompute, saveRecomputeSettings,
    retryFailedRecompute, applyRecompute, revertRecompute,
} from "../../services/RatingRecomputeService";
import Loading from "../../components/Loading";
import RecomputeTeamView from "./RecomputeTeamView";

// Admin status page for the rating-only Rating 3.0 v3 recompute (tasks/rating-recompute.md): the worker recomputes
// in the background without changing the site; Apply writes everything at once, Revert puts the old values back.

const C = {
    panel: "#232323", inset: "#1c1c1c", line: "#333", text: "#e2e8f0", muted: "rgba(255,255,255,0.55)",
    accent: "#f15c25", good: "#4ade80", bad: "#f87171", warn: "#fbbf24", run: "#f15c25", pending: "#3a3a3a",
};

const card: React.CSSProperties = {
    background: C.panel, border: `1px solid ${C.line}`, borderRadius: 12, padding: "16px 20px",
};
const label: React.CSSProperties = {
    fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: 0.6, color: C.muted,
};
const big: React.CSSProperties = { fontSize: "1.6rem", fontWeight: 800, color: "#fff", fontVariantNumeric: "tabular-nums" };
const btn = (kind: "primary" | "plain" | "danger" = "plain"): React.CSSProperties => ({
    cursor: "pointer", borderRadius: 8, padding: "7px 14px", fontWeight: 700, fontSize: "0.85rem",
    border: `1px solid ${kind === "primary" ? C.accent : kind === "danger" ? C.bad : C.line}`,
    background: kind === "primary" ? C.accent : "transparent",
    color: kind === "primary" ? "#fff" : kind === "danger" ? C.bad : C.text,
});
const th: React.CSSProperties = { textAlign: "left", padding: "7px 10px", ...label, borderBottom: "1px solid #2a2a2a", background: C.inset };
const td: React.CSSProperties = { padding: "7px 10px", borderBottom: "1px solid #262626", fontSize: "0.86rem" };
const num: React.CSSProperties = { ...td, textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" };

const fmtDur = (s: number | null): string => {
    if (s == null || !isFinite(s)) return "–";
    const h = Math.floor(s / 3600), m = Math.round((s % 3600) / 60);
    return h > 0 ? `${h} h ${m} min` : `${m} min`;
};
const fmtR = (v: number | null | undefined) => (v == null ? "–" : v.toFixed(3));
const fmtWhen = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString() : null);

const AdminRecomputePage: React.FC = () => {
    const { user, authLoaded } = useContext(AuthContext);
    const isAdmin = !!user && admins.includes(user.steamID);

    // null until the first status: the page opens on the season of the run in progress (else the current one)
    const [season, setSeason] = useState<number | null>(null);
    const [status, setStatus] = useState<RecomputeStatus | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [confirm, setConfirm] = useState<"apply" | "revert" | null>(null);
    const [message, setMessage] = useState<string | null>(null);
    const [concurrency, setConcurrency] = useState(2);
    const [respectWindow, setRespectWindow] = useState(true);
    const [settingsTouched, setSettingsTouched] = useState(false);

    const load = useCallback(async () => {
        try {
            const s = await getRecomputeStatus(season ?? undefined);
            setStatus(s);
            if (season == null) setSeason(s.season);
            setError(null);
            if (s.state && !settingsTouched) {
                setConcurrency(s.state.concurrency);
                setRespectWindow(s.state.respectWindow);
            }
        } catch {
            setError("Could not load the recompute status.");
        }
    }, [season, settingsTouched]);

    // Poll while the tab is visible: the page is meant to be left open during a run.
    useEffect(() => {
        if (!isAdmin) return;
        load();
        const id = window.setInterval(() => { if (document.visibilityState === "visible") load(); }, 4000);
        return () => window.clearInterval(id);
    }, [isAdmin, load]);

    const act = async (what: () => Promise<unknown>, done?: (r: any) => string) => {
        setBusy(true);
        setMessage(null);
        try {
            const r = await what();
            if (done) setMessage(done(r));
            await load();
        } catch {
            setError("The action failed. Check that you are still logged in as an admin.");
        } finally {
            setBusy(false);
            setConfirm(null);
        }
    };

    if (!authLoaded) return <div className="container text-center text-light my-5"><Loading /></div>;
    if (!isAdmin) return <div className="container text-light my-5">Admins only.</div>;
    if (!status || season == null) return <div className="container text-center text-light my-5">{error ?? <Loading />}</div>;

    const st = status.state;
    const thisSeason = st?.season === status.season;
    const running = !!st?.running && thisSeason;
    const c = status.report.counts;
    const total = c.pending + c.running + c.done + c.applied + c.skipped + c.failed;
    const computed = c.done + c.applied;
    const eta = status.report.avgSecondsPerMap != null && (c.pending + c.running) > 0
        ? ((c.pending + c.running) * status.report.avgSecondsPerMap) / Math.max(1, st?.concurrency ?? 1)
        : null;
    const parity = status.report.parity;
    const parityOk = parity.maps > 0 && parity.off === 0;

    let stateText = "Not started";
    let stateColor = C.muted;
    if (running && status.note) { stateText = status.note; stateColor = C.warn; }
    else if (running) { stateText = "Running"; stateColor = C.good; }
    else if (total > 0 && c.pending === 0 && c.running === 0) { stateText = "Finished"; stateColor = C.good; }
    else if (total > 0) { stateText = "Paused"; stateColor = C.warn; }

    const seg = (n: number, color: string, title: string) =>
        n > 0 && <div title={`${title}: ${n}`} style={{ width: `${(n / Math.max(1, total)) * 100}%`, background: color }} />;

    const apiBase = "https://api.powerstats.dk";

    return (
        <div className="container text-light" style={{ maxWidth: 1100, padding: "24px 16px 60px", display: "grid", gap: 16 }}>
            <div>
                <Link to="/admin" style={{ color: C.muted, fontSize: "0.85rem" }}>← Admin</Link>
                <h2 style={{ margin: "6px 0 4px", fontWeight: 800 }}>Rating 3.0 recompute</h2>
                <div style={{ color: C.muted, maxWidth: "75ch" }}>
                    Re-rates already parsed maps with the October v3 model and stores the per-event swing. It works in the
                    background and changes nothing on the site until you press Apply. Only Rating 3.0 and swing change;
                    kills, ADR, replays and clips are untouched.
                </div>
            </div>

            {error && <div style={{ ...card, borderColor: C.bad, color: C.bad }}>{error}</div>}
            {message && <div style={{ ...card, borderColor: C.good, color: C.good }}>{message}</div>}

            {/* Controls */}
            <div style={{ ...card, display: "flex", flexWrap: "wrap", gap: "12px 20px", alignItems: "center" }}>
                <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <span style={label}>Season</span>
                    <input id="recompute-season" type="number" value={season} min={29} max={currentSeason}
                        onChange={e => setSeason(Number(e.target.value) || currentSeason)}
                        style={{ width: 70, background: C.inset, color: C.text, border: `1px solid ${C.line}`, borderRadius: 6, padding: "4px 8px" }} />
                </label>
                <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <span style={label}>Maps at a time</span>
                    <select id="recompute-concurrency" value={concurrency}
                        onChange={e => { setConcurrency(Number(e.target.value)); setSettingsTouched(true); }}
                        style={{ background: C.inset, color: C.text, border: `1px solid ${C.line}`, borderRadius: 6, padding: "4px 8px" }}>
                        {[1, 2, 3, 4].map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                </label>
                <label style={{ display: "flex", gap: 8, alignItems: "center", cursor: "pointer" }}>
                    <input id="recompute-window" type="checkbox" checked={respectWindow}
                        onChange={e => { setRespectWindow(e.target.checked); setSettingsTouched(true); }} />
                    <span>Pause during match parsing (14-23 UTC)</span>
                </label>
                <div style={{ display: "flex", gap: 8, marginLeft: "auto", flexWrap: "wrap" }}>
                    {settingsTouched && st && (
                        <button style={btn()} disabled={busy}
                            onClick={() => act(async () => { await saveRecomputeSettings(concurrency, respectWindow); setSettingsTouched(false); })}>
                            Save settings
                        </button>
                    )}
                    {running ? (
                        <button style={btn()} disabled={busy} onClick={() => act(pauseRecompute)}>Pause</button>
                    ) : (
                        <button style={btn("primary")} disabled={busy}
                            onClick={() => act(async () => { const r = await startRecompute(season, concurrency, respectWindow); setSettingsTouched(false); return r; },
                                r => `Started season ${season}${r.added ? `: ${r.added} maps added to the queue` : ""}.`)}>
                            {total === 0 ? `Start season ${season}` : c.pending + c.running > 0 ? `Resume season ${season}` : "Add maps parsed since"}
                        </button>
                    )}
                </div>
            </div>

            {/* Progress */}
            <div style={{ ...card, display: "grid", gap: 12 }}>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "8px 24px", alignItems: "baseline" }}>
                    <div><div style={label}>State</div><div style={{ fontWeight: 700, color: stateColor }}>{stateText}</div></div>
                    <div><div style={label}>Computed</div><div style={big}>{computed} / {total}</div></div>
                    <div><div style={label}>Applied</div><div style={big}>{c.applied}</div></div>
                    <div><div style={label}>Skipped</div><div style={big}>{c.skipped}</div></div>
                    <div><div style={label}>Failed</div><div style={{ ...big, color: c.failed ? C.bad : "#fff" }}>{c.failed}</div></div>
                    <div><div style={label}>Time per map</div><div style={big}>{status.report.avgSecondsPerMap != null ? `${Math.round(status.report.avgSecondsPerMap)} s` : "–"}</div></div>
                    <div><div style={label}>Time left</div><div style={big}>{fmtDur(eta)}</div></div>
                </div>
                <div style={{ display: "flex", height: 12, borderRadius: 6, overflow: "hidden", background: C.pending }}>
                    {seg(c.applied, "#22c55e", "Applied")}
                    {seg(c.done, C.good, "Computed")}
                    {seg(c.running, C.run, "Running")}
                    {seg(c.skipped, "#6b7280", "Skipped")}
                    {seg(c.failed, C.bad, "Failed")}
                </div>
                <div style={{ color: C.muted, fontSize: "0.82rem" }}>
                    Time left assumes it keeps running; pauses during match parsing come on top.
                    {status.inParseWindow && respectWindow && " Match parsing is running now (14-23 UTC)."}
                </div>
                {status.current.length > 0 && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                        {status.current.map(m => (
                            <span key={m.gameId} style={{ background: C.inset, border: `1px solid ${C.line}`, borderRadius: 8, padding: "4px 10px", fontSize: "0.82rem" }}>
                                <b style={{ color: C.accent }}>●</b> {m.mapName ?? "?"} · game {m.gameId} · {m.seconds} s
                            </span>
                        ))}
                    </div>
                )}
            </div>

            {/* Checks */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 16 }}>
                <div style={card}>
                    <div style={label}>Check: maps already rated by v3</div>
                    <div style={{ ...big, color: parity.maps === 0 ? C.muted : parityOk ? C.good : C.bad }}>
                        {parity.maps === 0 ? "Not yet" : parityOk ? "Exact" : `${parity.off} off`}
                    </div>
                    <div style={{ color: C.muted, fontSize: "0.85rem" }}>
                        Maps played after 5 Oct were rated by v3 at parse time, so the recompute must give the same ratings.
                        {parity.maps > 0 && ` ${parity.maps} maps checked, largest difference ${fmtR(parity.maxAbsDelta)}.`}
                        {parity.maps > 0 && !parityOk && " Don't apply until this is understood."}
                    </div>
                </div>
                <div style={card}>
                    <div style={label}>Rating change on older maps</div>
                    <div style={big}>{status.report.changes.meanAbsDelta != null ? `±${status.report.changes.meanAbsDelta.toFixed(3)}` : "–"}</div>
                    <div style={{ color: C.muted, fontSize: "0.85rem" }}>
                        Average change per player per map over {status.report.changes.playerMaps} player-maps.
                        {" "}{status.report.changes.over005} changed by more than 0.05, {status.report.changes.over010} by more than 0.10.
                    </div>
                </div>
            </div>

            {/* Apply / revert */}
            <div style={{ ...card, display: "grid", gap: 10 }}>
                <div style={label}>Put it live</div>
                <div style={{ color: C.muted, fontSize: "0.88rem", maxWidth: "80ch" }}>
                    Apply writes every computed map at once (a few seconds). The old values are kept, so Revert can put them
                    back. You can apply more than once while the queue is still running; each time takes the maps computed since.
                    {st?.appliedAt && <> Last applied {fmtWhen(st.appliedAt)}.</>}
                    {st?.revertedAt && <> Last reverted {fmtWhen(st.revertedAt)}.</>}
                </div>
                {confirm === null ? (
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <button style={btn("primary")} disabled={busy || c.done === 0} onClick={() => setConfirm("apply")}>
                            Apply {c.done} computed maps
                        </button>
                        <button style={btn("danger")} disabled={busy || c.applied === 0} onClick={() => setConfirm("revert")}>
                            Revert {c.applied} applied maps
                        </button>
                    </div>
                ) : (
                    <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", background: C.inset, borderRadius: 8, padding: "10px 12px" }}>
                        <span>
                            {confirm === "apply"
                                ? `Write new Rating 3.0 and swing for ${c.done} maps of season ${status.season}? Players and fantasy see it after the follow-up steps below.`
                                : `Put the old values back for ${c.applied} maps of season ${status.season}?`}
                        </span>
                        <button style={btn(confirm === "apply" ? "primary" : "danger")} disabled={busy}
                            onClick={() => confirm === "apply"
                                ? act(() => applyRecompute(status.season), r => `Applied ${r.maps} maps (${r.rows} player rows). Now run the follow-up steps.`)
                                : act(() => revertRecompute(status.season), r => `Reverted ${r.maps} maps (${r.rows} player rows). Run the follow-up steps again.`)}>
                            {confirm === "apply" ? "Yes, apply" : "Yes, revert"}
                        </button>
                        <button style={btn()} disabled={busy} onClick={() => setConfirm(null)}>Cancel</button>
                    </div>
                )}
                <div style={{ color: C.muted, fontSize: "0.85rem" }}>
                    After Apply or Revert, run these (they take the admin password, so they are not buttons here):
                    <ol style={{ margin: "6px 0 0", paddingLeft: 20, display: "grid", gap: 4 }}>
                        <li><code>{apiBase}/PowerLiga/RecalculatePlayerTeamHistory?season={status.season}&password=…</code></li>
                        <li><code>{apiBase}/api/Fantasy/rescoreSeason?season={status.season}&password=…</code> (moves fantasy standings)</li>
                        <li>Restart the app so cached leaderboards pick up the new ratings.</li>
                    </ol>
                </div>
            </div>

            {/* One team: per map and season */}
            <RecomputeTeamView season={status.season} />

            {/* Biggest changes */}
            {status.report.topChanges.length > 0 && (
                <div style={{ ...card, padding: 0, overflow: "hidden" }}>
                    <div style={{ padding: "12px 16px", ...label }}>Biggest rating changes</div>
                    <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 520 }}>
                            <thead><tr><th style={th}>Player</th><th style={th}>Map</th><th style={{ ...th, textAlign: "right" }}>Old</th><th style={{ ...th, textAlign: "right" }}>New</th><th style={{ ...th, textAlign: "right" }}>Change</th></tr></thead>
                            <tbody>
                                {status.report.topChanges.map(ch => {
                                    const d = ch.new != null && ch.old != null ? ch.new - ch.old : null;
                                    return (
                                        <tr key={`${ch.gameId}-${ch.steamId}`}>
                                            <td style={td}><Link to={`/player/${ch.steamId}`} style={{ color: C.text }}>{ch.name || ch.steamId}</Link></td>
                                            <td style={td}>{ch.matchId ? <Link to={`/match/${ch.matchId}`} style={{ color: "#ee7d3f" }}>{ch.mapName ?? ch.gameId}</Link> : ch.mapName}</td>
                                            <td style={num}>{fmtR(ch.old)}</td>
                                            <td style={num}>{fmtR(ch.new)}</td>
                                            <td style={{ ...num, color: d == null ? C.muted : d >= 0 ? C.good : C.bad, fontWeight: 700 }}>{d == null ? "–" : `${d >= 0 ? "+" : ""}${d.toFixed(3)}`}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Skipped / failed */}
            {status.report.problems.length > 0 && (
                <div style={{ ...card, padding: 0, overflow: "hidden" }}>
                    <div style={{ padding: "12px 16px", display: "flex", alignItems: "center", gap: 12 }}>
                        <span style={label}>Skipped and failed maps</span>
                        {c.failed + c.skipped > 0 && (
                            <button style={{ ...btn(), marginLeft: "auto" }} disabled={busy}
                                onClick={() => act(() => retryFailedRecompute(status.season), r => `${r.requeued} maps are back in the queue.`)}>
                                Retry failed and skipped
                            </button>
                        )}
                    </div>
                    <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 520 }}>
                            <thead><tr><th style={th}>Status</th><th style={th}>Map</th><th style={th}>Reason</th><th style={{ ...th, textAlign: "right" }}>Tries</th></tr></thead>
                            <tbody>
                                {status.report.problems.map(p => (
                                    <tr key={p.gameId}>
                                        <td style={{ ...td, color: p.status === "failed" ? C.bad : C.muted, fontWeight: 700 }}>{p.status}</td>
                                        <td style={td}>{p.matchId ? <Link to={`/match/${p.matchId}`} style={{ color: "#ee7d3f" }}>{p.mapName ?? p.gameId}</Link> : p.mapName} <span style={{ color: C.muted }}>· {p.gameId}</span></td>
                                        <td style={{ ...td, color: C.muted }}>{p.reason}</td>
                                        <td style={num}>{p.attempts}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminRecomputePage;
